import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SessionView, SetupState } from '../../shared/contracts';
import { api, loadSession } from '../api';
import { Badge, ErrorNotice, Mark, Skeleton } from '../components/ui';

export function LandingPage() {
  const [session, setSession] = useState<SessionView | null>(null);
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextSession, nextSetup] = await Promise.all([
        loadSession(),
        api<SetupState>('/api/setup'),
      ]);
      setSession(nextSession);
      setSetup(nextSetup);
    } catch (nextError) {
      setError(nextError);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <main className="landing" id="main-content">
      <nav className="landing__nav" aria-label="Primary">
        <Link className="brand" to="/">
          <Mark />
          <span>RepoHost</span>
        </Link>
        <a href="https://github.com" target="_blank" rel="noreferrer" className="text-link">
          GitHub
        </a>
      </nav>
      <section className="hero">
        <div className="hero__eyebrow">
          <span className="status-dot" /> Owner-operated deployment control plane
        </div>
        <h1>From authorised repository to an honest deployment plan.</h1>
        <p>
          RepoHost inspects bounded repository metadata, explains what it detected, and provisions
          supported services in your own Render or Cloudflare account—only after you confirm the
          exact plan.
        </p>
        {error ? <ErrorNotice error={error} onRetry={() => void load()} /> : null}
        <div className="hero__actions">
          {session === null ? (
            <Skeleton lines={1} />
          ) : session.authenticated ? (
            <Link className="button" to="/app">
              Open deployment console <span aria-hidden="true">→</span>
            </Link>
          ) : (
            <a className="button" href="/api/auth/github/start?redirect=%2Fapp">
              Sign in with GitHub <span aria-hidden="true">→</span>
            </a>
          )}
          <a className="button button--quiet" href="#how-it-works">
            How it works
          </a>
        </div>
        {setup && !setup.ready ? (
          <div className="hero__setup">
            <Badge tone="warn">Setup incomplete</Badge>
            <span>
              The operator must finish server configuration before sign-in and deployment work.
            </span>
          </div>
        ) : null}
      </section>
      <section className="truth-strip" aria-label="Product boundaries">
        <div>
          <strong>No hosted build farm</strong>
          <span>Repository code runs only at the selected provider.</span>
        </div>
        <div>
          <strong>Free-plan guard</strong>
          <span>Paid resources are never selected by RepoHost v1.</span>
        </div>
        <div>
          <strong>Confirmation first</strong>
          <span>A digest-locked plan precedes every create request.</span>
        </div>
      </section>
      <section id="how-it-works" className="landing__section">
        <div className="section-heading">
          <span>How it works</span>
          <h2>A narrow path, with the risky boundaries made visible.</h2>
        </div>
        <ol className="process-list">
          <li>
            <span>01</span>
            <div>
              <h3>Connect the source</h3>
              <p>
                Sign in as an allowlisted owner and select repositories explicitly granted to the
                GitHub App.
              </p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <h3>Inspect, never execute</h3>
              <p>
                RepoHost parses bounded manifests and configuration files. It does not install
                dependencies or run repository scripts.
              </p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <h3>Review the contract</h3>
              <p>
                Correct commands, supply write-only secrets, and read provider prerequisites and
                free-tier limits.
              </p>
            </div>
          </li>
          <li>
            <span>04</span>
            <div>
              <h3>Provision in your account</h3>
              <p>
                A queued worker reconciles and creates the resource, then shows a URL only after the
                provider reports it live.
              </p>
            </div>
          </li>
        </ol>
      </section>
      <footer className="landing__footer">
        <span>RepoHost</span>
        <span>
          Static sites and stateless services only · no billing · no arbitrary shell execution
        </span>
      </footer>
    </main>
  );
}
